import { useEffect, useState } from 'react'
import { api, type ReslotPlan } from '@/api/client'
import { useInvalidatePosts } from '@/lib/postActions'
import { formatAbsolute } from '@/lib/time'
import { Button, Dialog, ErrorState, Skeleton, errorText, useToast } from '@/ui'

/** Moves a campaign's scheduled posts to the slots its current pacing would
 *  give them. A post's slot is fixed when it is approved, so changing the
 *  pacing never moves what is already queued — this does, once the operator
 *  has seen the table.
 *
 *  `preview` is a plan already fetched (right after a pacing save); without it
 *  the dialog asks for one when it opens. What is applied is exactly the plan
 *  shown: the server does not plan again, so the times do not shift between
 *  looking and pressing. */
export function ReslotDialog({
  campaignId,
  preview,
  onClose,
}: {
  campaignId: number
  preview?: ReslotPlan
  onClose: () => void
}) {
  const toast = useToast()
  const refresh = useInvalidatePosts()
  const [plan, setPlan] = useState<ReslotPlan | undefined>(preview)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [applyError, setApplyError] = useState<unknown>(null)

  useEffect(() => {
    if (preview) return
    let live = true
    api
      .reslotPreview(campaignId)
      .then((p) => live && setPlan(p))
      .catch((e: unknown) => live && setLoadError(e))
    return () => {
      live = false
    }
  }, [campaignId, preview])

  const moves = plan?.plan ?? []
  const skipped = plan?.skipped ?? []

  const apply = async () => {
    if (!plan) return
    setBusy(true)
    setApplyError(null)
    try {
      const res = await api.reslotApply(campaignId, plan.plan)
      await refresh()
      toast.success(
        res.skipped.length > 0
          ? `${res.applied}건을 옮겼다. ${res.skipped.length}건은 그 사이 바뀌어 그대로 뒀다`
          : `${res.applied}건을 옮겼다`,
      )
      onClose()
    } catch (e) {
      setApplyError(e)
    } finally {
      setBusy(false)
    }
  }

  const title = !plan
    ? '예약된 글을 새 규칙으로 다시 배치할까?'
    : moves.length > 0
      ? `예약된 ${moves.length}건을 새 규칙으로 다시 배치할까?`
      : '다시 배치할 예약이 없다'

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={title}
      description={
        moves.length > 0
          ? '예약 시각은 승인할 때 한 번 정해지고, 규칙을 바꿔도 따라 바뀌지 않는다. 글의 순서는 그대로 두고 시각만 옮긴다.'
          : plan
            ? '30분 안에 나가는 글은 옮기지 않는다. 나머지는 이미 지금 규칙에 맞는 시각이다.'
            : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {moves.length > 0 ? '그대로 둔다' : '닫기'}
          </Button>
          {moves.length > 0 && (
            <Button variant="primary" loading={busy} onClick={() => void apply()}>
              다시 배치
            </Button>
          )}
        </>
      }
    >
      {loadError != null ? (
        <ErrorState title="계획을 만들지 못했다" error={loadError} />
      ) : !plan ? (
        <Skeleton className="h-24" />
      ) : (
        <div className="space-y-3">
          {moves.length > 0 && (
            <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-table">
                <thead className="sticky top-0 bg-muted text-left text-meta text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">글</th>
                    <th className="px-3 py-1.5 font-medium">기존</th>
                    <th className="px-3 py-1.5 font-medium">새 시각</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {moves.map((m) => (
                    <tr key={m.draft_id}>
                      <td className="max-w-[14rem] truncate px-3 py-1.5" title={m.label}>
                        {m.label || `#${m.draft_id}`}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-muted-foreground">{formatAbsolute(m.from)}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums font-medium">
                        <span aria-hidden className="mr-1 text-muted-foreground">
                          →
                        </span>
                        {formatAbsolute(m.to)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {skipped.length > 0 && (
            <p className="text-meta text-muted-foreground">
              옮기지 않는 글 {skipped.length}건 — {skipped[0].reason}
              {skipped.some((s) => s.reason !== skipped[0].reason) && ' 외'}
            </p>
          )}
          {applyError != null && <p role="alert" className="text-table text-status-failed">{errorText(applyError)}</p>}
        </div>
      )}
    </Dialog>
  )
}
